import React, {
  useMemo,
  useState,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { Table, Column, HeaderCell, Cell } from "rsuite-table";
import "rsuite-table/dist/css/rsuite-table.css";
import styles from "./RemindersTable.module.scss";
import RFZOReminderModal from "./RFZOReminderModal";
import Button from "../UI/Buttons/Button/Button";
import axios, { baseUrl } from "../../axios/axios";
import { useNotification } from "../../contexts/NotificationContext";
import { useMarketplaceInfo } from "../../hooks/useMarketplaceInfo";
import SimpleModal from "../UI/Modals/SimpleModal/SimpleModal";
import { ReactComponent as BellIcon } from "../../assets/icons/bell-icon.svg";
import { ReactComponent as EditIcon } from "../../assets/icons/edit.svg";
import { ReactComponent as DeleteIcon } from "../../assets/icons/trash.svg";
import { ReactComponent as CopyIcon } from "../../assets/icons/copy-icon.svg";
import {
  formatRFZOPrice,
  calculateTotalRFZOPrice,
  createPriceTooltip,
} from "../../utils/rfzo/priceUtils";
import {
  isTherapyExpiringToday,
  isTherapyExpiringSoon,
  formatDate,
  createTherapyTooltip,
} from "../../utils/rfzo/dateUtils";
import { createItemsTooltip } from "../../utils/rfzo/itemUtils";
import { useUnifiedFilters } from "../../hooks/useUnifiedFilters";
import { SortHeader } from "../shared/SortHeader/SortHeader";
import { findColumnConfig } from "../../utils/table/tableColumns";
import { PatientReminderData, ReminderItem } from "../../types/reminderTypes";
import { effectivePersonalId } from "../../utils/rfzo/patientIdentifier";

// Define props interface for the new architecture
interface RemindersTableProps {
  patients: PatientReminderData[];
  onPatientsChange?: (
    patients: PatientReminderData[],
    operationType?: "note" | "therapy" | "delete",
  ) => void;
  onModalSubmit?: (
    message: string,
    updatedPatient?: PatientReminderData,
  ) => void;
  loading?: boolean;
  error?: string | null;
}

const allowedModifyStatuses = ["PENDING", "ORDERED", "WAITING"];

/**
 * Copy text to clipboard and show notification
 */
const copyToClipboard = async (
  text: string,
  showNotification: (type: "success" | "error", message: string) => void,
): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    showNotification("success", `LBO ${text} kopiran u clipboard`);
    return true;
  } catch (error) {
    console.error("Failed to copy to clipboard:", error);
    showNotification("error", "Greška pri kopiranju");
    return false;
  }
};

const RemindersTable: React.FC<RemindersTableProps> = ({
  patients,
  onPatientsChange = () => {},
  onModalSubmit,
  loading = false,
  error = null,
}) => {
  const { showNotification } = useNotification();

  // Use marketplace info hook
  const { branchMarketplaceId, marketplaceId, isReady } = useMarketplaceInfo();

  // Get sorting state and functions
  const { filters, toggleSort } = useUnifiedFilters();
  const currentSort = filters.sort || [];

  // Note editing state
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState("");
  const [savingNoteId, setSavingNoteId] = useState<number | null>(null);
  const [previousNoteValue, setPreviousNoteValue] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Confirmation modal states
  const [therapyConfirmModal, setTherapyConfirmModal] = useState<{
    show: boolean;
    data: PatientReminderData | null;
    newStatus: boolean;
  }>({ show: false, data: null, newStatus: false });

  const [deleteConfirmModal, setDeleteConfirmModal] = useState<{
    show: boolean;
    data: PatientReminderData | null;
  }>({ show: false, data: null });

  // Copyable LBO Cell Component
  const CopyableLBOCell: React.FC<{ rowData: PatientReminderData }> = ({
    rowData,
  }) => {
    const handleCopy = useCallback(
      async (e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent row click
        e.preventDefault(); // Prevent default behavior
        await copyToClipboard(rowData.lbo, showNotification);
      },
      [rowData.lbo],
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          e.stopPropagation();
          handleCopy(e as any);
        }
      },
      [handleCopy],
    );

    return (
      <div
        className={styles.copyableCell}
        onClick={handleCopy}
        onKeyDown={handleKeyDown}
        title="Kliknite da kopirate LBO"
        role="button"
        tabIndex={0}
      >
        {rowData.lbo}
      </div>
    );
  };

  // Use patients data directly with essential computed fields including price calculation
  const displayData = useMemo(() => {
    return patients.map((patient) => {
      // Calculate RFZO price metrics using existing utility
      const priceData = calculateTotalRFZOPrice(patient.items || []);
      const availableCount = priceData.itemCount - priceData.nullCount;
      const hasNullValues = priceData.nullCount > 0;

      return {
        ...patient,
        itemCount: patient.items.length,
        displayNote: patient.note || "/",
        // Essential price calculation for RFZO Price column
        totalRFZOPrice: priceData.total,
        rfzoPriceInfo: {
          ...priceData,
          availableCount,
          hasNullValues,
        },
      };
    });
  }, [patients]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (editingNoteId !== null && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editingNoteId]);

  // API Interaction Functions
  const deleteReminder = useCallback(
    async (personalId: string, email: string): Promise<boolean> => {
      // Guard against missing marketplace info
      if (!isReady) {
        console.warn("Cannot delete reminder: marketplace info not ready");
        showNotification("error", "Informacije o apoteci nisu dostupne");
        return false;
      }

      try {
        await axios.delete(
          baseUrl +
            `/rfzo_therapy_reminder/marketplace_id/${marketplaceId}/branch_marketplace_id/${branchMarketplaceId}/LBO/${personalId}/user_email/${email}`,
        );

        // Use callback to trigger parent refresh
        onPatientsChange(
          patients.filter(
            (patient) =>
              !(
                effectivePersonalId(patient) === personalId &&
                (patient.userEmail === email ||
                  patient.userResponse?.username === email)
              ),
          ),
          "delete",
        );

        showNotification("success", "Uspesno obrisan podsetnik");
        return true;
      } catch (error: any) {
        console.error("Delete failed:", error);

        // Enhanced error messages based on status code
        const errorMessage =
          error.response?.status === 409
            ? "Konflikt podataka - molimo osvežite stranicu"
            : error.response?.status === 403
              ? "Nemate dozvolu za ovu akciju"
              : error.response?.status >= 500
                ? "Server greška - pokušajte ponovo"
                : "Došlo je do greške prilikom brisanja";

        showNotification("error", errorMessage);
        return false;
      }
    },
    [
      onPatientsChange,
      patients,
      showNotification,
      isReady,
      marketplaceId,
      branchMarketplaceId,
    ],
  );

  const updateTherapyTaken = useCallback(
    async (reminderId: number, taken: boolean): Promise<boolean> => {
      try {
        await axios.put(
          baseUrl +
            `/rfzo_therapy_reminder/therapy_taken?reminderId=${reminderId}&taken=${taken}`,
        );

        // Optimistically update UI
        const updatedPatients = patients.map((patient) =>
          patient.id === reminderId
            ? { ...patient, therapyTaken: taken }
            : patient,
        );
        onPatientsChange(updatedPatients, "therapy");

        showNotification("success", "Status terapije uspešno ažuriran");
        return true;
      } catch (error: any) {
        console.error("Failed to update therapy taken status:", error);

        // Revert optimistic update on error
        const revertedPatients = patients.map((patient) =>
          patient.id === reminderId
            ? { ...patient, therapyTaken: !taken }
            : patient,
        );
        onPatientsChange(revertedPatients, "therapy");

        // Enhanced error messages based on status code
        const errorMessage =
          error.response?.status === 409
            ? "Konflikt podataka - molimo osvežite stranicu"
            : error.response?.status === 403
              ? "Nemate dozvolu za ovu akciju"
              : error.response?.status >= 500
                ? "Server greška - pokušajte ponovo"
                : "Došlo je do greške prilikom ažuriranja statusa";

        showNotification("error", errorMessage);
        return false;
      }
    },
    [onPatientsChange, patients, showNotification],
  );

  // Function to handle therapy taken checkbox click (shows confirmation)
  const handleTherapyTakenClick = useCallback(
    (rowData: PatientReminderData) => {
      const newStatus = !rowData.therapyTaken;
      setTherapyConfirmModal({
        show: true,
        data: rowData,
        newStatus,
      });
    },
    [],
  );

  // Function to confirm therapy taken status change
  const confirmTherapyTakenChange = useCallback(async () => {
    if (!therapyConfirmModal.data) return;

    const success = await updateTherapyTaken(
      therapyConfirmModal.data.id,
      therapyConfirmModal.newStatus,
    );

    if (success) {
      setTherapyConfirmModal({ show: false, data: null, newStatus: false });
    }
  }, [therapyConfirmModal, updateTherapyTaken]);

  // Function to cancel therapy taken confirmation
  const cancelTherapyTakenChange = useCallback(() => {
    setTherapyConfirmModal({ show: false, data: null, newStatus: false });
  }, []);

  const updateNote = useCallback(
    async (
      rowData: PatientReminderData,
      newNote: string,
    ): Promise<{ success: boolean; errorMessage?: string }> => {
      // Guard against missing marketplace info
      if (!isReady) {
        console.warn("Cannot update note: marketplace info not ready");
        return {
          success: false,
          errorMessage: "Informacije o apoteci nisu dostupne",
        };
      }

      setSavingNoteId(rowData.id);

      try {
        const therapyRequestDraft = {
          // The effective personal id (LBO, else JMBG), as the delete path
          // sends. The backend keys this PUT on COALESCE(NULLIF(LBO,""), jmbg),
          // so a JMBG-only row sent with the raw "" LBO resolved to nothing and
          // came back 404. userJmbg is deliberately not echoed: the therapy
          // update rewrites the stored JMBG whenever the request carries one.
          userLbo: effectivePersonalId(rowData),
          userFirstName: rowData.userFirstName,
          userSecondName: rowData.userSecondName,
          userEmail:
            rowData.userEmail ||
            rowData.userResponse?.username ||
            rowData.email,
        };

        // Transform items to match the format expected by the API
        const itemsRemap = (rowData.items || []).map((item: ReminderItem) => ({
          itemId: item.itemResponse?.id || item.itemId || item.id,
          articleName: item.itemResponse?.name || item.articleName,
          rfzoQuantity: item.rfzoQuantity || 1,
          rfzoParticipationPrice: item.rfzoParticipationPrice || 0,
        }));

        const requestPayload = {
          note: newNote,
          userPhone: rowData.userPhone || rowData.phone || "",
          branchMarketplaceId: branchMarketplaceId,
          marketplaceId: marketplaceId,
          therapyRequest: therapyRequestDraft,
          items: itemsRemap,
          rfzoTherapyReminderGroupsIds:
            rowData.rfzoTherapyReminderGroupsIds?.map?.((g: any) => g?.value) ||
            [],
        };

        if (process.env.NODE_ENV !== "production") {
          console.log("Update note payload:", requestPayload);
        }

        await axios.put(baseUrl + `/rfzo_therapy_reminder`, requestPayload, {
          headers: {
            "ApotekaRS-Branch-Marketplace-ID": branchMarketplaceId.toString(),
          },
        });
        return { success: true };
      } catch (error: any) {
        console.error("Failed to update note:", error);

        // Enhanced error logging and messaging
        const errorMessage =
          error.response?.status === 409
            ? "Konflikt podataka - molimo osvežite stranicu"
            : error.response?.status === 403
              ? "Nemate dozvolu za ovu akciju"
              : error.response?.status >= 500
                ? "Server greška - pokušajte ponovo"
                : "Došlo je do greške prilikom čuvanja napomene";

        console.error("Note update error details:", {
          status: error.response?.status,
          message: errorMessage,
          originalError: error,
        });

        return { success: false, errorMessage };
      } finally {
        setSavingNoteId(null);
      }
    },
    [branchMarketplaceId, marketplaceId, isReady],
  );

  // UI Interaction Functions
  const startEdit = useCallback(
    (rowData: PatientReminderData) => {
      if (
        !allowedModifyStatuses.includes(rowData.status) ||
        savingNoteId !== null
      )
        return;

      const noteValue = rowData.note === "/" ? "" : rowData.note;
      setPreviousNoteValue(noteValue);
      setEditingValue(noteValue);
      setEditingNoteId(rowData.id);
    },
    [savingNoteId],
  );

  const cancelEdit = useCallback(() => {
    setEditingNoteId(null);
    setEditingValue("");
    setPreviousNoteValue("");
  }, []);

  const saveNoteChanges = useCallback(
    async (rowData: PatientReminderData) => {
      // Don't save if nothing changed (handle "/" special case)
      const originalNote = rowData.note === "/" ? "" : rowData.note;
      if (originalNote === editingValue) {
        cancelEdit();
        return;
      }

      // Optimistically update UI
      const updatedNote = editingValue || "/";
      const updatedPatients = patients.map((patient) =>
        patient.id === rowData.id ? { ...patient, note: updatedNote } : patient,
      );
      console.log(
        "RemindersTable: Optimistic update - calling onPatientsChange",
        {
          patientId: rowData.id,
          newNote: updatedNote,
          updatedPatientsCount: updatedPatients.length,
        },
      );
      onPatientsChange(updatedPatients, "note");

      // Exit edit mode before API call
      setEditingNoteId(null);

      // Send API request
      console.log("RemindersTable: Sending API request for note update");
      const result = await updateNote(rowData, editingValue);
      console.log("RemindersTable: API request completed", {
        success: result.success,
      });

      if (result.success) {
        console.log(
          "RemindersTable: Note update successful - showing success notification",
        );
        showNotification("success", "Uspešno sačuvana napomena");
      } else {
        console.log(
          "RemindersTable: Note update failed - showing error notification",
        );
        showNotification(
          "error",
          result.errorMessage || "Došlo je do greške prilikom čuvanja napomene",
        );
        // Revert optimistic update on failure
        const revertedPatients = patients.map((patient) =>
          patient.id === rowData.id
            ? { ...patient, note: previousNoteValue || "/" }
            : patient,
        );
        onPatientsChange(revertedPatients, "note");
      }

      // Always clean up state
      cancelEdit();
    },
    [
      editingValue,
      previousNoteValue,
      cancelEdit,
      updateNote,
      showNotification,
      onPatientsChange,
      patients,
    ],
  );

  // Function to handle delete button click (shows confirmation)
  const handleDeleteClick = useCallback((rowData: PatientReminderData) => {
    setDeleteConfirmModal({
      show: true,
      data: rowData,
    });
  }, []);

  // Function to confirm deletion
  const confirmDelete = useCallback(async () => {
    if (!deleteConfirmModal.data) return;

    // A patient with neither identifier cannot be addressed by the endpoint: the
    // request would delete nothing and still answer 200.
    const personalId = effectivePersonalId(deleteConfirmModal.data);
    if (!personalId) {
      showNotification("error", "Došlo je do greške prilikom brisanja");
      return;
    }

    const success = await deleteReminder(
      personalId,
      deleteConfirmModal.data.email,
    );

    if (success) {
      setDeleteConfirmModal({ show: false, data: null });
    }
  }, [deleteConfirmModal, deleteReminder, showNotification]);

  // Function to cancel deletion
  const cancelDelete = useCallback(() => {
    setDeleteConfirmModal({ show: false, data: null });
  }, []);

  return (
    <>
      <Table
        renderEmpty={() => (
          <div className={styles.emptyState}>Podaci nisu pronađeni.</div>
        )}
        data={displayData}
        height={600}
        autoHeight
        bordered
        cellBordered
        hover
        rowClassName={(rowData: PatientReminderData) => {
          if (!rowData) return "";
          return rowData.therapyTaken ? styles.therapyTakenRow : "";
        }}
        // wordWrap
      >
        {/* Index column - Fixed, very narrow width */}
        <Column width={50} fixed="left" align="center">
          <HeaderCell>
            <span title="Redni broj">r. br.</span>
          </HeaderCell>
          <Cell verticalAlign="middle">
            {(rowData, rowIndex) => (rowIndex !== undefined ? rowIndex + 1 : 0)}
          </Cell>
        </Column>

        {/* ID column - Fixed, narrow width */}
        <Column width={60} fixed="left" align="center">
          <HeaderCell>ID</HeaderCell>
          <Cell verticalAlign="middle" dataKey="id" />
        </Column>

        {/* Name column - Flexible, high priority */}
        <Column flexGrow={1} minWidth={100}>
          <HeaderCell>
            <SortHeader
              config={findColumnConfig("userFirstName")!}
              currentSort={currentSort}
              onSort={toggleSort}
            />
          </HeaderCell>
          <Cell verticalAlign="middle">
            {(rowData: PatientReminderData) => {
              const expiringToday = isTherapyExpiringToday(
                rowData.willBeReadyTo,
              );
              const expiringSoon = isTherapyExpiringSoon(rowData.willBeReadyTo);

              return (
                <div className={styles.nameWithIcon}>
                  <span
                    title={createItemsTooltip(rowData.items)}
                    style={{ cursor: "help" }}
                  >
                    {rowData.name}
                  </span>
                  {(expiringToday || expiringSoon) && (
                    <span
                      className={styles.bellIconContainer}
                      title={
                        expiringToday
                          ? "Terapija ističe danas"
                          : "Terapija uskoro ističe"
                      }
                    >
                      <BellIcon
                        className={`${styles.bellIcon} ${
                          expiringToday
                            ? styles.bellIconToday
                            : styles.bellIconSoon
                        }`}
                      />
                    </span>
                  )}
                </div>
              );
            }}
          </Cell>
        </Column>

        {/* Phone column - Flexible, medium priority */}
        {/* <Column flexGrow={1} minWidth={120}>
          <HeaderCell>Broj telefona</HeaderCell>
          <Cell verticalAlign="middle" dataKey="phone" />
        </Column> */}

        {/* LBO column - Fixed width */}
        <Column width={120}>
          <HeaderCell>LBO</HeaderCell>
          <Cell verticalAlign="middle">
            {(rowData: PatientReminderData) => (
              <CopyableLBOCell rowData={rowData} />
            )}
          </Cell>
        </Column>

        {/* NEW: Therapy Due Date column */}
        <Column width={120}>
          <HeaderCell>
            <SortHeader
              config={findColumnConfig("willBeReadyFrom")!}
              currentSort={currentSort}
              onSort={toggleSort}
            />
          </HeaderCell>
          <Cell verticalAlign="middle">
            {(rowData: PatientReminderData) => (
              <span
                title={createTherapyTooltip(
                  rowData.willBeReadyFrom,
                  rowData.willBeReadyTo,
                )}
                style={{ cursor: "help" }}
              >
                {formatDate(rowData.willBeReadyFrom)}
              </span>
            )}
          </Cell>
        </Column>

        {/* RFZO Price column */}
        <Column width={100} minWidth={80} align="right">
          <HeaderCell>RFZO Cena</HeaderCell>
          <Cell verticalAlign="middle">
            {(rowData: PatientReminderData) => {
              const priceInfo = rowData.rfzoPriceInfo;
              if (!priceInfo) {
                return <span>{formatRFZOPrice(rowData.totalRFZOPrice)}</span>;
              }

              const tooltip = createPriceTooltip(
                priceInfo.availableCount,
                priceInfo.itemCount,
                priceInfo.hasNullValues,
              );

              const displayValue =
                priceInfo.availableCount === 0
                  ? "N/A"
                  : formatRFZOPrice(
                      rowData.totalRFZOPrice,
                      priceInfo.hasNullValues,
                    );

              return <span title={tooltip}>{displayValue}</span>;
            }}
          </Cell>
        </Column>

        {/* Therapy Taken column - Fixed width */}
        <Column width={120} align="center">
          <HeaderCell>
            <SortHeader
              config={findColumnConfig("therapyTaken")!}
              currentSort={currentSort}
              onSort={toggleSort}
            />
          </HeaderCell>
          <Cell verticalAlign="middle">
            {(rowData: PatientReminderData) => {
              const isDisabled = !allowedModifyStatuses.includes(
                rowData.status,
              );

              return (
                <div className={styles.checkboxContainer}>
                  <input
                    type="checkbox"
                    checked={rowData.therapyTaken || false}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleTherapyTakenClick(rowData);
                    }}
                    disabled={isDisabled}
                    className={styles.checkbox}
                    title={
                      isDisabled
                        ? "Ne možete izmeniti status"
                        : rowData.therapyTaken
                          ? "Označeno kao preuzeto"
                          : "Označi kao preuzeto"
                    }
                  />
                </div>
              );
            }}
          </Cell>
        </Column>

        {/* Note column - Flexible, highest priority */}
        <Column flexGrow={1} minWidth={140}>
          <HeaderCell>Napomena</HeaderCell>
          <Cell verticalAlign="middle">
            {(rowData: PatientReminderData) => {
              const isEditing = editingNoteId === rowData.id;
              const isEditable = allowedModifyStatuses.includes(rowData.status);
              const isSaving = savingNoteId === rowData.id;

              if (isEditing) {
                return (
                  <div className={styles.editableCell}>
                    <input
                      ref={inputRef}
                      type="text"
                      className={styles.editInput}
                      value={editingValue}
                      onChange={(e) => setEditingValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          saveNoteChanges(rowData);
                        } else if (e.key === "Escape") {
                          e.preventDefault();
                          cancelEdit();
                        }
                      }}
                      disabled={isSaving}
                      maxLength={500}
                      placeholder="Dodaj napomenu"
                    />
                    <div className={styles.editActions}>
                      <Button
                        size="xs"
                        appearance="primary"
                        onClick={() => saveNoteChanges(rowData)}
                        disabled={isSaving}
                        title="Sačuvaj (Enter)"
                        className={styles.saveButton}
                      >
                        ✓
                      </Button>
                      <Button
                        size="xs"
                        appearance="subtle"
                        onClick={cancelEdit}
                        disabled={isSaving}
                        title="Otkaži (Escape)"
                        className={styles.cancelButton}
                      >
                        ✕
                      </Button>
                    </div>
                    {isSaving && <span className={styles.spinner}></span>}
                  </div>
                );
              }

              return (
                <div
                  className={`${styles.noteCell} ${
                    isEditable ? styles.editable : ""
                  } ${isSaving ? styles.saving : ""}`}
                  onClick={() => isEditable && !isSaving && startEdit(rowData)}
                  role={isEditable && !isSaving ? "button" : undefined}
                  aria-label={
                    isEditable && !isSaving ? "Izmeni napomenu" : undefined
                  }
                  tabIndex={isEditable && !isSaving ? 0 : undefined}
                  onKeyDown={(e) =>
                    isEditable &&
                    !isSaving &&
                    e.key === "Enter" &&
                    startEdit(rowData)
                  }
                  title={
                    isEditable && !isSaving
                      ? "Kliknite da izmenite napomenu"
                      : ""
                  }
                >
                  {rowData.note}
                  {/* Visual indicator for editable fields */}
                  {isEditable && !isSaving && (
                    <EditIcon
                      className={styles.editIndicator}
                      title="Kliknite da izmenite napomenu"
                    />
                  )}
                  {isSaving && <span className={styles.spinner}></span>}
                </div>
              );
            }}
          </Cell>
        </Column>

        {/* Actions column - Fixed width, right-fixed for accessibility */}
        <Column width={96} fixed="right" align="center">
          <HeaderCell>Akcija</HeaderCell>
          <Cell verticalAlign="middle">
            {(rowData: PatientReminderData) => {
              const isDisabled =
                !allowedModifyStatuses.includes(rowData.status) ||
                savingNoteId !== null;

              return (
                <div className={styles.buttonContainer}>
                  <RFZOReminderModal
                    customButton={{
                      class: `${styles.openButton} ${
                        isDisabled ? styles.disabledIconButton : ""
                      }`,
                      data: rowData,
                      buttonType: "icon",
                      text: "Otvori / Izmeni",
                      mode: "preview",
                      disabled: isDisabled,
                      icon: (
                        <EditIcon
                          className={`${styles.editIcon} ${
                            isDisabled ? styles.disabledIcon : ""
                          }`}
                        />
                      ),
                      title: "Otvori / Izmeni",
                    }}
                    onSubmit={onModalSubmit}
                  />
                  <RFZOReminderModal
                    initialData={rowData}
                    duplicateButton={{
                      class: `${styles.openButton} ${
                        isDisabled ? styles.disabledIconButton : ""
                      }`,
                      buttonType: "icon",
                      text: "Ponovi",
                      disabled: isDisabled,
                      icon: (
                        <CopyIcon
                          className={`${styles.copyIcon} ${
                            isDisabled ? styles.disabledIcon : ""
                          }`}
                        />
                      ),
                      title: "Ponovi podsetnik",
                    }}
                    onSubmit={onModalSubmit}
                  />
                  <Button
                    disabled={isDisabled}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      handleDeleteClick(rowData);
                    }}
                    buttonType="icon"
                    title="Obriši"
                    aria-label="Obriši"
                    className={isDisabled ? styles.disabledIconButton : ""}
                  >
                    <DeleteIcon
                      className={`${styles.deleteIcon} ${
                        isDisabled ? styles.disabledIcon : ""
                      }`}
                    />
                  </Button>
                </div>
              );
            }}
          </Cell>
        </Column>
      </Table>

      {/* Therapy Taken Confirmation Modal */}
      {therapyConfirmModal.show && (
        <SimpleModal
          show={therapyConfirmModal.show}
          backdropClickHandler={cancelTherapyTakenChange}
          className={styles.confirmationModal}
        >
          <div className={styles.confirmationContent}>
            <h3 className={styles.confirmationTitle}>
              {therapyConfirmModal.newStatus
                ? "Potvrda preuzimanja terapije"
                : "Potvrda otkazivanja preuzimanja"}
            </h3>
            <p className={styles.confirmationMessage}>
              {therapyConfirmModal.newStatus
                ? `Da li ste sigurni da želite da označite terapiju za pacijenta "${therapyConfirmModal.data?.name}" kao preuzetu?`
                : `Da li ste sigurni da želite da otkazujete preuzimanje terapije za pacijenta "${therapyConfirmModal.data?.name}"?`}
            </p>
            <div className={styles.confirmationButtons}>
              <Button
                buttonType="blue"
                onClick={confirmTherapyTakenChange}
                className={styles.confirmButton}
              >
                Da
              </Button>
              <Button
                buttonType="white"
                onClick={cancelTherapyTakenChange}
                className={styles.cancelButton}
              >
                Ne
              </Button>
            </div>
          </div>
        </SimpleModal>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmModal.show && (
        <SimpleModal
          show={deleteConfirmModal.show}
          backdropClickHandler={cancelDelete}
          className={styles.confirmationModal}
        >
          <div className={styles.confirmationContent}>
            <h3 className={styles.confirmationTitle}>Potvrda brisanja</h3>
            <p className={styles.confirmationMessage}>
              Da li ste sigurni da želite da obrišete podsetnik za pacijenta "
              {deleteConfirmModal.data?.name}"?
            </p>
            <div className={styles.confirmationButtons}>
              <Button
                buttonType="red"
                onClick={confirmDelete}
                className={styles.confirmButton}
              >
                Da, obriši
              </Button>
              <Button
                buttonType="white"
                onClick={cancelDelete}
                className={styles.cancelButton}
              >
                Otkaži
              </Button>
            </div>
          </div>
        </SimpleModal>
      )}
    </>
  );
};

export default RemindersTable;
